import React, {useEffect, useState} from 'react';
import {Button, Card, CardContent, CardHeader, FormControlLabel, Radio, RadioGroup, Stack,} from "@mui/material";
import {FormattedMessage, useIntl} from "react-intl";
import {RequestVisitorDto} from "../../../../../modules/vms/dto/RequestVisitorDto";
import {VisitRequestDto} from "../../../../../modules/vms/dto/VisitRequestDto";
import PreviousNextButton from "../../../../../components/PreviousNextButton";
import {Language} from "../../../../../modules/common/enums/Language";
import {ImageUtils} from "../../../../../utils/ImageUtils";
import dayjs from "dayjs";
import {Formats} from "../../../../../constants/Formats";
import {RoutePath} from "../../../../../constants/RoutePath";
import {VMSCacheKey} from "../../../../../modules/vms/constants/VMSCacheKey";
import {DictItemDto} from "../../../../../modules/dict/dto/DictItemDto";
import {FileApi} from "../../../../../modules/ftp/api/FileApi";
import {HttpStatus} from "../../../../../modules/common/enums/HttpStatus";
import {ConsentLetterConfig} from "../../../../../modules/vms/constants/ConsentLetterConfig";

export type RegisterConsentFormProps = {
    onPrevious: () => void;
    onSubmit: (fileId: string) => void;
    requestVisitor: RequestVisitorDto;
    visitRequest: VisitRequestDto;
    purposeTypes: Array<DictItemDto>;
    isSubmitting: boolean;

};

// 检查签名图片的定时任务
let checkSignImageInterval: NodeJS.Timeout;

const RegisterConsentForm: React.FC<RegisterConsentFormProps> = (props) => {
    const intl = useIntl();

    const [consentLetterLanguage, setConsentLetterLanguage] = useState(Language.Enum.zhCN);

    const [consentLetterImgZh, setConsentLetterImgZh] = useState('');
    const [consentLetterImgEn, setConsentLetterImgEn] = useState('');
    const [consentLetterImg, setConsentLetterImg] = useState('');

    const [visitPlace, setVisitPlace] = useState('');
    const [visitorName, setVisitorName] = useState('');
    const [visitPurpose, setVisitPurpose] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [isShanghaiSite, setIsShanghaiSite] = useState(false);

    // 英文和中文的布局不同，所以签名的图片要跟源图片合并，需要考虑参数变量
    const signImageYZh = 2280;
    const signImageYEn = 2680;

    useEffect(() => {

        const visitRequest = props.visitRequest;
        console.log(visitRequest);
        // 如果是上海的站点，则需要显示上海的ConsentLetter
        const isShanghaiSite = visitRequest.siteCode.startsWith('SH');
        setIsShanghaiSite(isShanghaiSite)

        generateConsentLetter(isShanghaiSite, true);
        return () => {
            if (checkSignImageInterval) {
                clearInterval(checkSignImageInterval);
            }
        }

    }, []);
    const doFetchVisitContents = (isShanghaiSite: boolean, isZhFlag: boolean) => {
        const visitRequest = props.visitRequest;
        const requestVisitor = props.requestVisitor;

        let selectedPlaceStr = visitRequest.siteName;
        if (visitRequest.places && visitRequest.places.length > 0) {
            selectedPlaceStr = selectedPlaceStr + ": " + visitRequest.places.map((place) => {
                return place.locationName + "/" + place.areaName;
            }).join(',');
        }
        setVisitPlace(selectedPlaceStr);

        const visitorName = requestVisitor.lastName;
        setVisitorName(visitorName);

        const visitPurpose = visitRequest.purpose;
        setVisitPurpose(visitPurpose);

        const visitContents = new Array<string>();
        visitContents.push(selectedPlaceStr);
        visitContents.push(visitorName);
        if (isShanghaiSite) {
            visitContents.push(requestVisitor.company);
        }
        visitContents.push(visitPurpose);
        if (isShanghaiSite) {
            if (!isZhFlag) {
                visitContents.push(" ");//上海的英文Consent Letter多一个换行
            }
            // visitContents.push("host Name");
            visitContents.push(visitRequest.hostLastName);

            // visitContents.push("access Card Number");
            visitContents.push(requestVisitor.accessCardNumber);
        }

        return visitContents;
    }

    const generateConsentLetter = (isShanghaiSite: boolean, isZhFlag: boolean) => {
        let config;
        if (isZhFlag) {
            config = isShanghaiSite ? ConsentLetterConfig.SH_ZH : ConsentLetterConfig.BJ_ZH;
        } else {
            config = isShanghaiSite ? ConsentLetterConfig.SH_EN : ConsentLetterConfig.BJ_EN
        }

        const visitContents = doFetchVisitContents(isShanghaiSite, isZhFlag);

        const visitContentsImageConfig = config.visitContentsImage;
        const visitContentsImage = ImageUtils.covertStrToImage(visitContents, visitContentsImageConfig.width, visitContentsImageConfig.height, visitContentsImageConfig.fontSize, visitContentsImageConfig.lineSpace);
        const signDateStrArray = new Array<string>();
        signDateStrArray.push(dayjs().format(Formats.DATE_HYPHEN));
        const signDateImageConfig = config.signDateImage;
        const signDateImage = ImageUtils.covertStrToImage(signDateStrArray, signDateImageConfig.width, signDateImageConfig.height, signDateImageConfig.fontSize, signDateImageConfig.lineSpace);
        ImageUtils.getImageBase64(config.path, (backgroundImg) => {
            // 拼凑通知图片和签名图片
            ImageUtils.stitchPicture(backgroundImg, visitContentsImage, visitContentsImageConfig.x, visitContentsImageConfig.y, (stitchImage) => {
                // 拼凑签署日期
                ImageUtils.stitchPicture(stitchImage, signDateImage, signDateImageConfig.x, signDateImageConfig.y, (consentLetter) => {

                    if (isZhFlag) {
                        setConsentLetterImgZh(consentLetter);
                    } else {
                        setConsentLetterImgEn(consentLetter);
                    }
                    setConsentLetterImg(consentLetter);
                })
            })
        });
    }

    const handleChangeConsentLetterLanguage = (value: string) => {
        console.log(value);
        setConsentLetterLanguage(value);
        if (value === Language.Enum.enUS) {
            if (consentLetterImgEn === '') {
                generateConsentLetter(isShanghaiSite, false);
            } else {
                setConsentLetterImg(consentLetterImgEn);
            }
        } else {
            if (consentLetterImgZh === '') {
                generateConsentLetter(isShanghaiSite, true);

            } else {
                setConsentLetterImg(consentLetterImgZh);
            }
        }
        // console.log('handleChangeConsentLetterLanguage');
        // console.log(consentLetterImg);
    }

    const handleScreenProjection = async () => {

        // 缓存当前的同意书
        localStorage.setItem(VMSCacheKey.CONSENT_LETTER, consentLetterImg);

        // @ts-ignore
        const screenDetails = await window.getScreenDetails().catch(e => {
            console.error(e);
            return null;
        });
        console.log(screenDetails);

        let targetScreen = window.screen;

        for (let screenDetail of screenDetails.screens) {
            if (!screenDetail.isPrimary) {
                targetScreen = screenDetail;
                break;
            }
        }
        console.log(targetScreen);

        // @ts-ignore
        const features = "left=" + targetScreen.availLeft + ",top=" + targetScreen.availTop +
            ",width=" + targetScreen.availWidth + ",height=" + targetScreen.availHeight + ",fullscreen";

        // 英文和中文的布局不同，所以签名的图片要跟源图片合并，需要考虑参数变量
        let signImageY;

        if (consentLetterLanguage === Language.Enum.enUS) {
            signImageY = isShanghaiSite ? ConsentLetterConfig.SH_EN.signDateImage.y
                : ConsentLetterConfig.BJ_EN.signDateImage.y
        } else {
            signImageY = isShanghaiSite ? ConsentLetterConfig.SH_ZH.signDateImage.y
                : ConsentLetterConfig.BJ_ZH.signDateImage.y;

        }

        const pageUrl = RoutePath.VMS_VISITOR_MANAGE_CHECKIN_SIGN + "?signImageY=" + signImageY;
        window.open(pageUrl, '_blank', features);

        // 等待签名返回
        checkSignImageInterval = setInterval(() => {
            const signedConsentLetter = localStorage.getItem(VMSCacheKey.CONSENT_LETTER);
            if (signedConsentLetter && signedConsentLetter.length > consentLetterImg.length) {
                clearInterval(checkSignImageInterval);

                // 清空缓存的签名照片
                localStorage.removeItem(VMSCacheKey.CONSENT_LETTER);
                // 更新本地的签名照片
                setConsentLetterImg(signedConsentLetter);
            }
        }, 1000);
    }


    const handleReset = () => {
        if (consentLetterLanguage === Language.Enum.enUS) {
            setConsentLetterImg(consentLetterImgEn);
        } else {
            setConsentLetterImg(consentLetterImgZh);
        }
    }

    const handleSubmit = async () => {
        setIsLoading(true)
        const responseResult = await FileApi.uploadBase64File(consentLetterImg);
        if (responseResult) {
            if (responseResult.status === HttpStatus.SUCCESS) {
                props.onSubmit(responseResult.data);
                setIsLoading(false);

            } else if (responseResult.status) {
                setIsLoading(false);
            }
        }

    }

    return (
        <div>
            <Stack spacing={2} style={{height: '60vh'}}>
                <Card style={{height: '100%'}}>
                    <CardHeader
                        action={
                            <Stack direction={'row'} spacing={2}>
                                <RadioGroup row value={consentLetterLanguage}
                                            onChange={(e) => handleChangeConsentLetterLanguage(e.target.value)}>
                                    {
                                        Language.List.map((item, index) => {
                                            return (
                                                <FormControlLabel key={index} value={item.value} control={<Radio/>}
                                                                  label={intl.formatMessage({id: item.label})}/>
                                            )
                                        })
                                    }
                                </RadioGroup>
                                <Button variant={'contained'} color="primary" size={'small'}
                                        onClick={handleScreenProjection}>
                                    <FormattedMessage id={'COMMON_SCREEN_PROJECTION'}/>
                                </Button>
                                <Button variant={'outlined'} color="primary" size={'small'} onClick={handleReset}>
                                    <FormattedMessage id={'COMMON_RESET'}/>
                                </Button>
                            </Stack>
                        }
                    />
                    <CardContent style={{overflow: 'auto', height: '55vh'}}>
                        <img alt={'consentLetterImg'} src={consentLetterImg} style={{width: '100%'}}/>
                    </CardContent>
                </Card>
            </Stack>
            <br/>
            <PreviousNextButton onPrevious={props.onPrevious} onNext={handleSubmit}
                                disabled={isLoading || props.isSubmitting}
                                nextText={intl.formatMessage({id: "COMMON_SUBMIT"})}/>
        </div>
    );
};

export default RegisterConsentForm;
